import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import http from "../utils/http";

interface UseStripeCardDetailsOptions {
  stripeCustomerId?: string;
  setHasAddedCard: (has: boolean) => void;
}

interface StripeCustomerData {
  stripeCustomerId?: string;
  customerId?: string;
}

interface SavedCardsData {
  hasSavedCard?: boolean;
}

export function useStripeCardDetails({
  stripeCustomerId,
  setHasAddedCard
}: UseStripeCardDetailsOptions) {
  const queryClient = useQueryClient();

  // Step 1: Create Stripe customer mutation - only when needed
  const createCustomerMutation = useMutation<StripeCustomerData, Error, void>({
    mutationFn: async () => {
      const res = await http.post("providers/stripe/id");
      return res.data;
    }
  });

  // Step 2: Determine the effective customer ID
  const effectiveCustomerId =
    stripeCustomerId ||
    createCustomerMutation.data?.stripeCustomerId ||
    createCustomerMutation.data?.customerId;

  // Step 3: Check for saved cards - runs when we have a customer ID
  const checkCardsQuery = useQuery<SavedCardsData | null, Error>({
    queryKey: ["stripe", "savedCards", effectiveCustomerId],
    queryFn: async (): Promise<SavedCardsData | null> => {
      if (!effectiveCustomerId) return null;
      const res = await http.post("/stripe/check-saved-cards", {
        customerId: effectiveCustomerId
      });
      return res.data;
    },
    enabled: !!effectiveCustomerId // Only run when we have a customer ID
  });

  // Step 4: Create customer if needed - using React Query's dependent pattern
  const shouldCreateCustomer =
    !stripeCustomerId &&
    !effectiveCustomerId &&
    !createCustomerMutation.isPending;
  const createCustomerQuery = useQuery({
    queryKey: ["stripe", "createCustomer", shouldCreateCustomer],
    queryFn: async () => {
      if (!shouldCreateCustomer) return null;
      // Trigger the mutation and return the result
      const result = await createCustomerMutation.mutateAsync();
      return result;
    },
    enabled: shouldCreateCustomer,
    retry: false
  });

  // Step 5: Handle setHasAddedCard based on query result
  const hasSavedCard = !!(
    checkCardsQuery.data && checkCardsQuery.data.hasSavedCard
  );
  useEffect(() => {
    if (checkCardsQuery.isSuccess && checkCardsQuery.data) {
      setHasAddedCard(hasSavedCard);
    }
  }, [
    checkCardsQuery.isSuccess,
    checkCardsQuery.data,
    hasSavedCard,
    setHasAddedCard
  ]);

  return {
    isChecking:
      createCustomerMutation.isPending ||
      checkCardsQuery.isLoading ||
      createCustomerQuery.isLoading,
    error:
      createCustomerMutation.error ||
      checkCardsQuery.error ||
      createCustomerQuery.error,
    customerId: effectiveCustomerId,
    hasSavedCard,
    isCreatingCustomer:
      createCustomerMutation.isPending || createCustomerQuery.isLoading,
    isCheckingCards: checkCardsQuery.isLoading
  };
}
